import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { EllipsisVertical, ExternalLink, Search } from "lucide-react";

import { useAccountEntitlements } from "@/entitlements/queries/useAccountEntitlements";
import { specifiedUserIsFPEmployee } from "@/util";
import { UserDisplay } from "@/components/users/UserDisplay";
import { ColumnConfig, Table } from "@/design-system/components/Table/Table";
import { Badge } from "@/design-system/components/Badge/Badge";
import { Input } from "@/design-system/components/Input/Input";
import { DropdownMenu } from "@/design-system/components/DropdownMenu/DropdownMenu";
import { User } from "@/components/users/types";
import FreshpaintUser from "@/components/users/FreshpaintUser";
import { RoleAdmin } from "@/components/users/inviteUser";

import { roleToReadableName } from "../roleOptions";
import { MemberListEmptyState } from "./TeamPageEmptyStates";

type MemberListProps = {
  currentMember: FreshpaintUser;
  members: User[];
  onClickDelete: (user: User) => void;
  onClickChangeRole: (user: User) => void;
  onClickFormsAccess: (user: User) => void;
  onClickEnvAccess: (user: User) => void;
};

export const MemberList = React.memo(function MemberList({
  currentMember,
  members,
  onClickDelete,
  onClickChangeRole,
  onClickFormsAccess,
  onClickEnvAccess,
}: MemberListProps) {
  const { data: accountEntitlements } = useAccountEntitlements();

  const formsEnabled = accountEntitlements?.account_access?.some(
    (access) => access.entitlement_key === "forms",
  );

  const [search, setSearch] = useState("");
  const trimmedSearch = search.trim().toLowerCase();

  const filteredMembers = useMemo(
    () =>
      trimmedSearch
        ? members.filter((member) =>
            member.email.toLowerCase().includes(trimmedSearch),
          )
        : members,
    [members, trimmedSearch],
  );

  const columnConfigs = useMemo<ColumnConfig<User>[]>(
    () => [
      {
        label: "",
        CellContent: ({ row }) =>
          row.uid === currentMember.uid ? (
            <Badge variant="success" text="You" />
          ) : null,
      },
      {
        label: "Email",
        CellContent: ({ row }) => <UserDisplay user={row} variant="stacked" />,
      },
      {
        label: "Role",
        CellContent: ({ row }) => (
          <div>
            <span>{roleToReadableName(row.role)}</span>
            {specifiedUserIsFPEmployee(row.uid) && (
              <p className="text-textSubtle text-xs">Member is a super-user</p>
            )}
          </div>
        ),
      },
      {
        label: "Added",
        getCellText: (row) => format(new Date(row.created_at), "MMMM d, yyyy"),
      },
      {
        label: "Actions",
        align: "center",
        CellContent: ({ row }) => (
          <PositionedActionsMenu
            hideRole={
              row.uid === currentMember.uid ||
              !currentMember.hasAdminAccess() ||
              specifiedUserIsFPEmployee(row.uid)
            }
            onClickRole={() => onClickChangeRole(row)}
            hideEnvAccess={false}
            onClickEnvAccess={() => onClickEnvAccess(row)}
            hideForms={
              !formsEnabled ||
              row.uid === currentMember.uid ||
              row.role === RoleAdmin ||
              !currentMember.hasAdminAccess() ||
              specifiedUserIsFPEmployee(row.uid)
            }
            onClickForms={() => onClickFormsAccess(row)}
            hideDelete={
              row.uid === currentMember.uid || !currentMember.hasAdminAccess()
            }
            onClickDelete={() => onClickDelete(row)}
          />
        ),
      },
    ],
    [
      currentMember,
      onClickChangeRole,
      onClickEnvAccess,
      onClickFormsAccess,
      onClickDelete,
      formsEnabled,
    ],
  );

  return (
    <div className="space-y-4">
      <h3 className="text-label-sm text-textSubtle font-medium">Members</h3>
      <Input
        type="search"
        autoComplete="off"
        placeholder="Search by email"
        leadingIcon={<Search className="text-iconDefault" size={16} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="lg:max-w-1/2"
      />
      <Table
        columnConfigs={columnConfigs}
        EmptyState={MemberListEmptyState}
        getRowId={(row) => row.uid}
        rows={filteredMembers}
      />
      <p className="text-sm">
        Learn more about{" "}
        <span className="inline-flex items-center gap-1">
          <a
            className="text-textActionSelected hover:text-textActionHover underline"
            href="https://documentation.freshpaint.io/your-freshpaint-account/settings/rbac"
          >
            role-based access control
          </a>
          <ExternalLink className="text-iconDefault" size={16} />
        </span>
      </p>
    </div>
  );
});

type ActionMenuItem = {
  label: string;
  handler: () => void;
};

type PositionedActionsMenuProps = {
  hideRole: boolean;
  hideForms: boolean;
  hideDelete: boolean;
  hideEnvAccess: boolean;
  onClickRole: () => void;
  onClickForms: () => void;
  onClickDelete: () => void;
  onClickEnvAccess: () => void;
};

function PositionedActionsMenu({
  hideRole,
  hideForms,
  hideDelete,
  hideEnvAccess,
  onClickRole,
  onClickForms,
  onClickDelete,
  onClickEnvAccess,
}: PositionedActionsMenuProps) {
  const actions = useMemo(() => {
    const items: ActionMenuItem[] = [];
    if (!hideRole) items.push({ label: "Edit Role", handler: onClickRole });
    if (!hideEnvAccess)
      items.push({
        label: "Edit Environment Access",
        handler: onClickEnvAccess,
      });
    if (!hideForms)
      items.push({ label: "Edit Forms Access", handler: onClickForms });
    if (!hideDelete) items.push({ label: "Delete", handler: onClickDelete });
    return items;
  }, [
    hideRole,
    hideForms,
    hideDelete,
    hideEnvAccess,
    onClickRole,
    onClickForms,
    onClickDelete,
    onClickEnvAccess,
  ]);

  if (actions.length === 0) return null;

  return (
    <DropdownMenu
      options={actions}
      getOptionLabel={(action) => action.label}
      onSelectOption={(action) => action.handler()}
      icon={<EllipsisVertical className="h-5 w-5" />}
      triggerProps={{ className: "h-8 w-8 rounded-md p-1.5 hover:bg-gray-100" }}
    />
  );
}
